import { AsyncPipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { AfterContentInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialogConfig } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { b64toBlob } from '@app/api/core/utils/file.util';
import { detailExpandAnimation, OAHelper } from '@app/api/core/utils/helper/offerteantraege-helper';
import { getActivateLabel, getDeactivateLabel } from '@app/api/core/utils/helper/person-helper';
import { isNullOrUndefined } from '@app/api/core/utils/util';
import { ElektronischeKommunikationDialogComponent } from '@app/shared/components/dialog/elektronische-kommunikation-dialog.component/elektronische-kommunikation-dialog.component';
import { KundenportalConfirmationDialogComponent } from '@app/shared/components/dialog/kundenportal-confirmation-dialog/kundenportal-confirmation-dialog.component';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { ReminderComponent } from '@app/shared/components/reminder/reminder.component';
import { ThrottleEventDirective } from '@app/shared/directives/throttle-event.directive';
import { FormatAdressPipe } from '@app/shared/pipes/formatadress.pipe';
import { FormatDatePipe } from '@app/shared/pipes/formatdate.pipe';
import { FormatPersonPipe } from '@app/shared/pipes/formatperson.pipe';
import { ActiveTab } from '@app/shared/services/state/model/o30/activetab';
import { DialogStatus, Mode } from '@app/shared/services/ui/dialog/dialog.types';
import { DisplayOption, MessageType } from '@app/shared/services/ui/message/message';
import { ListTyp } from '@app/views/vertriebscockpit/offerteantraege/offerteantraege.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DocumentHandedBy, DocumentHandedType, ElektronischeKommunikationResult, ObjektTyp, PolizzeResult, Status, VkeTyp } from '@o30/entities';
import { EMPTY, of } from 'rxjs';
import { first, map, switchMap, take } from 'rxjs/operators';
import { BasicPolizzeListComponent } from '../basic-polizze-list/basic-polizze-list.component';

type ForWhom = 'forKunde' | 'forBeitragszahler';

@UntilDestroy()
@Component({
  selector:        'app-offertstagetwo',
  templateUrl:     './offertstagetwo.component.html',
  styleUrls:       ['./offertstagetwo.component.scss'],
  animations:      [detailExpandAnimation],
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports:         [
    NgIf,
    MatTable,
    MatSort,
    NgFor,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    ThrottleEventDirective,
    MatTooltip,
    ReminderComponent,
    NgClass,
    MatButton,
    NgTemplateOutlet,
    MatProgressSpinner,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    NoDataComponent,
    AsyncPipe,
    FormatDatePipe,
    FormatAdressPipe
  ]
})
// BeratungComponent BeratungEmpfehungComponent
export class OffertstagetwoComponent extends BasicPolizzeListComponent implements OnInit,
                                                                                  OnDestroy,
                                                                                  AfterContentInit {

  statusEnum = Status;
  generatingBeratungsProtokoll: number;

  kontaktdatenStatusMap = new Map();

  constructor() {
    super();
  }

  ngAfterContentInit(): void {
    this.listData.loadingFinished.subscribe(finished => {
      if (finished) {
        // Mehrfach vorkommende Polizzen entfernen (spart doppelte Abfragen)
        const listDataUnique = this.listData.dataSource.data.filter((v, i, a) => a.findIndex(t => (t.data.id === v.data.id)) === i);
        for (const element of listDataUnique) {
          if (this.kontaktdatenStatusMap.has(element.data.id)) {
            return this.kontaktdatenStatusMap.get(element.data.id);
          } else {
            this.dataService.getPolizzeKontaktDatenpruefung(element.data.id)
              .pipe(take(1), untilDestroyed(this))
              .subscribe(payload => {
                if (this.messageService.handlePayloadReturnSuccess(payload)) {
                  this.kontaktdatenStatusMap.set(element.data.id, payload.data);
                }
              });
          }
        }
      }
    });
  }

  public getStatusText(status: Status): string {
    switch (status) {
      case this.statusEnum.DURCHGEFUEHRT:
        return 'durchgeführt';
      case this.statusEnum.VERSENDET:
        return 'versendet';
      case this.statusEnum.AUSGEHAENDIGT:
        return 'ausgehändigt';
      case this.statusEnum.NICHT_AUSGEHAENDIGT:
        return 'nicht ausgehändigt';
      case this.statusEnum.NICHT_DURCHGEFUEHRT:
        return 'nicht durchgeführt';
      case this.statusEnum.NICHT_BENOETIGT:
        return 'nicht benötigt';
    }
  }

  public getStatusIcon(status: Status): string {
    switch (status) {
      case this.statusEnum.DURCHGEFUEHRT:
      case this.statusEnum.VERSENDET:
      case this.statusEnum.AUSGEHAENDIGT:
        return 'check_circle';
      case this.statusEnum.NICHT_DURCHGEFUEHRT:
      case this.statusEnum.NICHT_AUSGEHAENDIGT:
        return 'cancel';
      case this.statusEnum.NICHT_BENOETIGT:
        return 'offline_pin';
    }
  }

  public getStatusIconClass(status: Status): string {
    switch (status) {
      case this.statusEnum.DURCHGEFUEHRT:
      case this.statusEnum.VERSENDET:
      case this.statusEnum.NICHT_BENOETIGT:
      case this.statusEnum.AUSGEHAENDIGT:
        return 'green-icon';
      case this.statusEnum.NICHT_DURCHGEFUEHRT:
      case this.statusEnum.NICHT_AUSGEHAENDIGT:
        return 'red-icon';
    }
  }

  public antragSpeichernAllowed(row: PolizzeResult): boolean {
    return this.isIddCompleted(row) && this.isElektronischeKommunikationCompleted(row) && this.isOffenlegungCompleted(row);
  }

  private isIddCompleted(row: PolizzeResult): boolean {
    return this.iddBefreit ||
      (
        (row.ipidkid === Status.AUSGEHAENDIGT || row.ipidkid === Status.NICHT_BENOETIGT) &&
        (row.beratungsprotokoll === Status.AUSGEHAENDIGT || row.beratungsprotokoll === Status.NICHT_BENOETIGT)
      );
  }

  private isElektronischeKommunikationCompleted(row: PolizzeResult): boolean {
    const completedForKnd = !row.elektronischeKommunikation || row.elektronischeKommunikation.completed;
    const completedForBtz = !row.elektronischeKommunikationBeitragszahler || row.elektronischeKommunikationBeitragszahler.completed;
    return this.iddBefreit || (completedForKnd && completedForBtz);
  }

  private isOffenlegungCompleted(row: PolizzeResult): boolean {
    return !row.offenlegungAnswerNeeded || row.offenlegungAnswer === 'yes';
  }

  isKundenportalDisabled(row: PolizzeResult): boolean {
    return row?.kundeIsPortalUser;
  }

  public sendIPIDKID(row: PolizzeResult) {
    const dialogtitle = 'Informationsblätter versenden';
    const infotext = `Ich bestätige mittels Betätigen des Buttons „Senden“, dass der Kunde vorab der Übermittlung der ${this.getInfoblattString(row)} und der ` +
      'Datenschutzinformation in elektronischer Form an die erfasste E-Mail-Adresse ausdrücklich zugestimmt hat.';

    const config: MatDialogConfig = {
      data: {title: dialogtitle}
    };
    this.dialogService.displaySendEmailDialog(row.personId, infotext, 'ipidkid', row.id, config)
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this)
      )
      .subscribe(emailDialogResult => {
        // TODO this will fail on warnings without displaying anything
        if (emailDialogResult?.status === 'success') {
          row.ipidkid = Status.AUSGEHAENDIGT;
        }
      });
  }

  public beratungsProtokollAllowed(row: PolizzeResult): boolean {
    return row.ipidkid === Status.AUSGEHAENDIGT
      && this.isElektronischeKommunikationCompleted(row)
      && this.kundenPortalCompleted(row)
      && (this.kontaktdatenStatusMap.get(row.id) === 'KONTAKT_EDITED'
        || this.kontaktdatenStatusMap.get(row.id) === 'KONTAKT_CHECKED'
        || this.kontaktdatenStatusMap.get(row.id) === 'KONTAKT_KEINE_BEKANNTGABE')
      && this.isOffenlegungCompleted(row);
  }

  private kundenPortalCompleted(row: PolizzeResult): boolean {
    return (row.kundeIsPortalUser || !!row.kundenPortalRequestAnswer);
  }

  public openIPIDKID(row: PolizzeResult) {
    const dialogtitle = 'Informationsblätter';
    const infotext = 'Sie können die einzelnen Produktdatenblätter mit den untenstehenden Links in Ihrem PDF-Programm öffnen und anschließend ausdrucken.';
    const savetext = `Ich bestätige mittels Betätigen des Buttons „OK“, dass ich die ${this.getInfoblattString(row)} und die Datenschutzinformation in Papier übergeben habe.`;
    const config: MatDialogConfig = {
      data: {title: dialogtitle}
    };

    this.dataService.getProduktdatenblatt(row.id)
      .pipe(
        take(1),
        untilDestroyed(this),
        switchMap(payload => {
          if (this.messageService.handlePayloadReturnSuccess(payload)) {
            return this.dialogService.displayIpidKidProtokollDialog(infotext, savetext, payload.data, [], config)
              .afterClosed();
          } else {
            return EMPTY;
          }
        })
      )
      .subscribe(_dialogIpidkidResult => {
          if (_dialogIpidkidResult && _dialogIpidkidResult.status === 'success') {
            this.dataService.putPolizzeProduktinfoblatt(row.id, {
                handedBy: DocumentHandedBy.PRINT,
                type:     DocumentHandedType.PRODUKTDATENBLATT
              })
              .pipe(
                take(1),
                untilDestroyed(this)
              )
              .subscribe(_produktinfoBlattResult => {
                if (this.messageService.handlePayloadReturnSuccess(_produktinfoBlattResult)) {
                  row.ipidkid = Status.AUSGEHAENDIGT;
                }
              });
          }
        }
      );
  }

  private getInfoblattString(polizzeResult: PolizzeResult) {
    return (polizzeResult?.vke?.typ === VkeTyp.LEBEN)
      ? 'Basis-/Produktinformationsblätter'
      : 'Produktinformationsblätter';
  }

  public gotoRisikoanalyse(row: PolizzeResult) {
    return this.routerService.routeRisikouebersicht(row.personId);
  }

  public sendBeratungsprotokoll(row: PolizzeResult) {
    const dialogtitle = 'Beratungsprotokoll versenden';
    const infotext = 'Ich bestätige mittels Betätigen des Buttons „Senden“, dass der Kunde vorab der Übermittlung des Beratungsprotokolles in elektronischer Form an die erfasste E-Mail-Adresse ausdrücklich zugestimmt hat.';

    const config: MatDialogConfig = {
      data: {title: dialogtitle}
    };

    this.dialogService.displaySendEmailDialog(row.personId, infotext, 'beratungsprotokoll', row.id, config)
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this)
      )
      .subscribe(emailDialogResult => {
        if (emailDialogResult?.status === 'success') {
          row.beratungsprotokoll = Status.AUSGEHAENDIGT;
        }
      });
  }

  public openBeratungsprotokoll(row: PolizzeResult) {
    this.generatingBeratungsProtokoll = row.id;

    const dialogtitle = 'Beratungsprotokoll';
    const infotext = 'Sie können das Beratungsprotokoll in Ihrem PDF-Programm öffnen und anschließend ausdrucken.';
    const savetext = 'Ich bestätige mittels Betätigen des Buttons „OK“, dass ich das Beratungsprotokoll in Papier übergeben habe.';
    const config: MatDialogConfig = {
      data: {title: dialogtitle}
    };

    this.dataService.getBeratungsprotokoll(row.id)
      .pipe(
        take(1),
        untilDestroyed(this),
        switchMap(payload => {
          this.generatingBeratungsProtokoll = undefined;
          if (this.messageService.handlePayloadReturnSuccess(payload)) { // Base64 PDF
            return this.dialogService.displayIpidKidProtokollDialog(infotext, savetext, [],
                payload.data.map(_item => ({
                  key:   _item.key,
                  value: b64toBlob(_item.value, 'application/pdf')
                })), config)
              .afterClosed();
          } else {
            return EMPTY;
          }
        })
      )
      .subscribe(_dialogResult => {
          if (_dialogResult && _dialogResult.status === 'success') {
            this.dataService.putPolizzeBeratungsprotokoll(row.id, {
                handedBy: DocumentHandedBy.PRINT,
                type:     DocumentHandedType.BERATUNGSPROTOKOLL
              })
              .pipe(
                take(1),
                untilDestroyed(this)
              )
              .subscribe(_beratungsprotokollResult => {
                if (this.messageService.handlePayloadReturnSuccess(_beratungsprotokollResult)) {
                  row.beratungsprotokoll = Status.AUSGEHAENDIGT;
                }
              });
          }
        }
      );
  }

  public createAntrag(row: PolizzeResult): void {
    if (!this.iddBefreit && row.risikoanalyse !== Status.DURCHGEFUEHRT && row.risikoanalyse !== Status.NICHT_BENOETIGT) {
      const dialogtitle = 'Risikoanalyse nicht durchgeführt';
      const confirmText = 'Es wurde noch keine Risikoanalyse durchgeführt. Antrag trotzdem erstellen?';
      const checkboxText = 'Keine Risikoanalyse gewünscht';
      const config: MatDialogConfig = {
        data: {title: dialogtitle}
      };
      this.dialogService.displayConfirmDialog(confirmText, checkboxText, config)
        .afterClosed()
        .pipe(
          take(1),
          untilDestroyed(this)
        )
        .subscribe(result => {
          if (result && result.status === 'success') {
            this.saveAntrag(row.id);
          }
        });
    } else {
      this.saveAntrag(row.id);
    }
  }

  private saveAntrag(id: number): void {
    this.dataService.savePolizzeAntrag(id)
      .pipe(
        first(),
        untilDestroyed(this))
      .subscribe(payload => {
        if (this.messageService.handlePayloadReturnSuccess(payload, {displayOption: DisplayOption.MONITOR_ONLY_INFO})) {
          this.messageService.showSnackBar({
            icon: 'done',
            text: `Antrag speichern erfolgreich durchgeführt`,
            type: MessageType.SUCCESS
          });
          // why not reload just one item?
          this.loadList(); // BeratungEmpfehlung neu laden
          this.listData.reloadListSubject.next(ListTyp.ANTRAG); // Anträge neu laden
          this.llapi.patchValue('layout.activeTab', ActiveTab.OfferteAntraege_ANTRAEGE);
        }
      });
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--override superclass--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  public updateLabel(anzRowsPerEntry = 3, maxViewed = 10): void {
    if (this.iddBefreit) {
      anzRowsPerEntry = 2; // idd befreit hat nur 2 rows
    }
    this.currentPaginationLabel = OAHelper.getPaginationLabel(this.paginator.pageIndex, this.listData.dataSource.filteredData.length, anzRowsPerEntry, maxViewed);
  }

  get displayedColumns() {
    const columns = this.tableColumns.map(c => c.columnDef);
    columns.unshift('expand');

    // Wenn nicht berechtigt → Spalten nicht anzeigen
    if (this.bearbeitenErlaubt) {
      columns.push('aktionen');
      columns.push('erstellen');
    }
    return columns;
  }

  getAnzRowsPerEntry(): number {
    return this.iddBefreit ? 2 : 3;
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[-- elektronische kommunikation --],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  openElekKomDialog(polizze: PolizzeResult, forWhom: ForWhom, value: boolean = true): void {
    const result = this.getEkResult(polizze, forWhom);
    const polizzeId = polizze.id;
    this.dataService.getPerson(result.personId)
      .pipe(
        untilDestroyed(this),
        take(1),
        map(_payload => _payload.data),
        map(_person => new FormatPersonPipe().transform(_person, undefined, true)),
        map(
          _personFormatted => ({
            data:     {
              title: `Elektronische Kommunikation für ${_personFormatted}`
            },
            maxWidth: '800px'
          })
        ),
        switchMap(_config => this.reactiveDialogService.open(ElektronischeKommunikationDialogComponent, _config, value, {
            polizzeId,
            result
          })
          .afterClosed())
      )
      .subscribe(_dialogResult => {
        if (_dialogResult && _dialogResult.status === 'success') {
          (polizze[this.getEkResultKey(forWhom)] as ElektronischeKommunikationResult) = _dialogResult.data;
        }
      });
  }

  isDeactivateDisabled(polizze: PolizzeResult, forWhom: ForWhom): boolean {
    const ek = this.getEkResult(polizze, forWhom);
    return ek.pending;
  }

  private getEkResult(polizze: PolizzeResult, forwhom: ForWhom): ElektronischeKommunikationResult {
    return polizze ? polizze[this.getEkResultKey(forwhom)] as any as ElektronischeKommunikationResult : undefined;
  }

  private getEkResultKey(forwhom: ForWhom): keyof PolizzeResult {
    return forwhom === 'forKunde' ? 'elektronischeKommunikation' : 'elektronischeKommunikationBeitragszahler';
  }

  getElekKomStatusIcon(polizze: PolizzeResult, forWhom: ForWhom): 'check_circle' | 'cancel' | 'settings' {
    const ek = this.getEkResult(polizze, forWhom);
    if (ek.pending || ek.status === 'activated') {
      return 'check_circle';
    }
    if (ek.status === 'deactivated') {
      return 'cancel';
    }
    if (ek.completed) {
      return 'check_circle';
    } else {
      return 'cancel';
    }
  }

  getElekKomStatusText(polizze: PolizzeResult, forWhom: ForWhom): string {
    const ek = this.getEkResult(polizze, forWhom);
    if (!ek || isNullOrUndefined(ek.status)) {
      return 'nicht vorhanden';
    }
    if (ek.pending && ek.status === 'deactivated') {
      return 'noch nicht bestätigt';
    }
    switch (ek.status) {
      case 'activated':
        return 'vorhanden';
      case 'deactivated':
        return 'nicht vorhanden';
      default:
        return 'unbekannt';
    }
  }

  getElekKomStatusClass(polizze: PolizzeResult, forWhom: ForWhom): 'green-icon' | 'red-icon' | 'yellow-icon' {
    const ek = this.getEkResult(polizze, forWhom);
    if (ek.pending) {
      return 'yellow-icon';
    }
    if (ek.status === 'activated' || ek.completed) {
      return 'green-icon';
    }
    return 'red-icon';
  }

  getActivateLabel(polizze: PolizzeResult, forWhom: ForWhom): string {
    const ek = this.getEkResult(polizze, forWhom);
    return getActivateLabel(ek);
  }

  getDeactivateLabel(polizze: PolizzeResult, forWhom: ForWhom): string {
    const ek = this.getEkResult(polizze, forWhom);
    return getDeactivateLabel(ek);
  }

  getKundenPortalStatusText(polizze: PolizzeResult): string {
    return polizze.kundeIsPortalUser ? 'aktiv' : 'nicht aktiv';
  }

  getOffenlegungspflichtText(polizze: PolizzeResult): string {
    return polizze.offenlegungAnswer === 'yes' ? 'zugestimmt' : 'nicht zugestimmt';
  }

  getOffenlegungspflichtIconClass(polizze: PolizzeResult): 'green-icon' | 'red-icon' {
    return polizze.offenlegungAnswer === 'yes'?  'green-icon' : 'red-icon';
  }

  getOffenlegungspflichtIcon(polizze: PolizzeResult): 'check_circle' | 'cancel' {
    if (polizze.offenlegungAnswer === 'yes') {
      return 'check_circle';
    }
    return 'cancel';
  }

  getKundenPortalStatusIconClass(polizze: PolizzeResult): 'green-icon' | 'red-icon' {
    return !isNullOrUndefined(polizze.kundenPortalRequestAnswer) || polizze.kundeIsPortalUser
      ? 'green-icon'
      : 'red-icon';
  }

  getKundenPortalStatusIcon(polizze: PolizzeResult): 'check_circle' | 'cancel' {
    if (polizze.kundeIsPortalUser || polizze.kundenPortalRequestAnswer === 'yes') {
      return 'check_circle';
    }
    if (polizze.kundenPortalRequestAnswer === 'no') {
      return 'cancel';
    }
    return (polizze.kundeIsPortalUser)
      ? 'check_circle'
      : 'cancel';
  }

  openKundenPortalDialog(polizze: PolizzeResult): void {
    this.reactiveDialogService.open(KundenportalConfirmationDialogComponent,
        {
          width: '500px' // TODO fix
        })
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this),
        switchMap(_result => _result && _result.status === 'success'
          ? this.dataService.putPolizzeKundenPortalAnswer(polizze.id, _result.data)
          : EMPTY)
      )
      .subscribe(_payload => polizze.kundenPortalRequestAnswer = _payload && _payload.data
        ? 'yes'
        : 'no'
      );
  }

  acceptOffenlegungspflicht(polizze: PolizzeResult): void {
    this.dataService.putOffenlegungspflichtAnswer(polizze.id, true)
      .subscribe(_payload => polizze.offenlegungAnswer = _payload && _payload.data
        ? 'yes'
        : 'no'
      );
  }

  getKontaktdatenStatus(polizzeId: number): { text: string, icon: string, iconClass: string } {
    let status = this.kontaktdatenStatusMap.get(polizzeId);

    switch (status) {
      case 'KONTAKT_MISSING':
        return {
          text:      'nicht vorhanden',
          icon:      'cancel',
          iconClass: 'red-icon'
        };
      case 'KONTAKT_EDITED':
        return {
          text:      'aktuell',
          icon:      'check_circle',
          iconClass: 'green-icon'
        };
      case 'KONTAKT_CHECKED':
        return {
          text:      'geprüft',
          icon:      'check_circle',
          iconClass: 'green-icon'
        };
      case 'KONTAKT_CHECK_MISSING':
        return {
          text:      'nicht geprüft',
          icon:      'cancel',
          iconClass: 'green-icon'
        };
      case 'KONTAKT_KEINE_BEKANNTGABE':
        return {
          text:      'keine Bekanntgabe',
          icon:      'check_circle',
          iconClass: 'green-icon'
        };
    }

    return {
      text:      'unbekannt',
      icon:      'cancel',
      iconClass: 'red-icon'
    };
  }

  kontaktdatenKeineBekanntgabe(polizzeId: number): void {
    // KONTAKT_KEINE_BEKANNTGABE = lokaler Status
    this.kontaktdatenStatusMap.set(polizzeId, 'KONTAKT_KEINE_BEKANNTGABE');
  }

  kontaktdatenPruefen(personId: number, polizzeId: number) {
    this.dataService.putPolizzeKontaktDatenpruefung(polizzeId)
      .pipe(
        take(1),
        untilDestroyed(this))
      .subscribe(pruefungPayload => {
        if (this.messageService.handlePayloadReturnSuccess(pruefungPayload)) {
          this.kontaktdatenErfassen(personId, polizzeId);
        }
      });
  }

  kontaktdatenErfassen(personId: number, polizzeId: number) {
    this.dialogService.displayPersonErfassenDialog({
        objektTyp:  ObjektTyp.KUNDE,
        objId:      personId,
        kfa:        false,
        skipPolice: true,
        mode:       Mode.PREVENTNAMEEDIT
      })
      .afterClosed()
      .pipe(
        switchMap(_dialogErfassenResult => {
          if (_dialogErfassenResult?.data && _dialogErfassenResult.statusId === DialogStatus.SAVE_SUCCESS) {
            const person = _dialogErfassenResult.data;
            return this.dataService.putPerson(person);
          } else {
            return of(undefined); // Falls Speichern fehlgeschlagen bzw nicht gespeichert wird, Kontaktdatenprüfung trotzdem aufrufen
          }
        }),
        switchMap(personPayload => {
          if (personPayload) {
            this.messageService.handlePayload(personPayload, {displayOption: DisplayOption.SNACKBAR});
          }
          return this.dataService.getPolizzeKontaktDatenpruefung(polizzeId);
        }),
        take(1),
        untilDestroyed(this))
      .subscribe(payload => {
        if (this.messageService.handlePayloadReturnSuccess(payload)) {
          this.kontaktdatenStatusMap.set(polizzeId, payload.data);
        }
      });
  }

  isKontaktdatenMissing(polizzeId: number): boolean {
    const status = this.kontaktdatenStatusMap.get(polizzeId);
    return status === 'KONTAKT_MISSING' || status === 'KONTAKT_KEINE_BEKANNTGABE';
  }

  beratungChange(row: PolizzeResult) {
    this.polizzeChange(row, 'beratung');
  }

}
